import React, { useState, useEffect } from 'react';

function UserProfile({ user, onBack }) {
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    bloodGroup: user?.bloodGroup || '',
    emergencyPhone: user?.emergencyPhone || '',
    localPharmacy: user?.localPharmacy || '',
    dateOfBirth: user?.dateOfBirth || '',
    photoUrl: localStorage.getItem(`patient_photo_${user?.id}`) || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Auth/profile/${user.id}`);
        const data = await res.json();
        setForm({
          name: data.name || '',
          phone: data.phone || '',
          bloodGroup: data.bloodGroup || '',
          emergencyPhone: data.emergencyPhone || '',
          localPharmacy: data.localPharmacy || '',
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : ''
        });
      } catch { }
    };
    fetchProfile();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Auth/update-profile/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bloodGroup: form.bloodGroup,
          emergencyPhone: form.emergencyPhone,
          localPharmacy: form.localPharmacy
        })
      });
      if (res.ok) setMessage('✅ Profile updated successfully!');
      else setMessage('❌ Failed to update profile.');
    } catch { setMessage('❌ Cannot connect to server.'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F4F7FC]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="bg-white px-4 py-4 flex items-center gap-3 border-b border-gray-100 shadow-sm">
        <button onClick={onBack} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-full">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className="font-black text-[#0D1B2A] text-lg">User Profile</h1>
      </div>

      <div className="px-4 py-5 space-y-4">
        {/* Avatar with photo upload */}
        <div className="flex flex-col items-center py-4">
          <div className="relative mb-3">
            {form.photoUrl ? (
              <img src={form.photoUrl} alt="Profile"
                className="w-24 h-24 rounded-full object-cover shadow-lg border-4 border-white" style={{ boxShadow: '0 4px 20px rgba(11,60,149,0.2)' }} />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#0B3C95] flex items-center justify-center text-white font-black text-3xl shadow-lg border-4 border-white">
                {user?.name?.charAt(0) || 'A'}
              </div>
            )}
            {/* Camera button overlay */}
            <label className="absolute bottom-0 right-0 w-8 h-8 bg-[#0B3C95] rounded-full flex items-center justify-center cursor-pointer shadow-md border-2 border-white active:scale-95 transition-all">
              <span className="material-symbols-outlined text-white" style={{ fontSize: '16px', fontVariationSettings: '"FILL" 1' }}>photo_camera</span>
              <input type="file" accept="image/*" capture="user" className="hidden"
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = ev => {
                      setForm(prev => ({ ...prev, photoUrl: ev.target.result }));
                      localStorage.setItem(`patient_photo_${user.id}`, ev.target.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }} />
            </label>
          </div>
          <p className="text-xs text-gray-400 mb-2">Tap camera to update photo</p>
          <p className="font-black text-[#0D1B2A] text-xl">{form.name}</p>
          <p className="text-gray-400 text-sm mt-0.5">{user?.patientUid}</p>
          <span className="mt-2 px-3 py-1 bg-[#0B3C95]/10 text-[#0B3C95] text-xs font-bold rounded-full">PATIENT</span>
        </div>

        {message && (
          <div className={`p-3 rounded-xl text-sm ${message.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
            {message}
          </div>
        )}

        {/* Read-only fields */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Account Info</p>
          {[
            { label: 'Full Name', value: form.name, icon: 'person' },
            { label: 'Phone Number', value: form.phone, icon: 'call' },
            { label: 'Date of Birth', value: form.dateOfBirth || 'Not set', icon: 'cake' },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0B3C95]/10 rounded-xl flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[#0B3C95]" style={{ fontSize: '18px' }}>{item.icon}</span>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-400">{item.label}</p>
                <p className="text-sm font-bold text-[#0D1B2A]">{item.value}</p>
              </div>
              <span className="text-xs text-gray-300 font-semibold">Read only</span>
            </div>
          ))}
        </div>

        {/* Editable fields */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Health Info</p>

          <div>
            <label className="text-xs font-bold text-[#0D1B2A] mb-1.5 block">Blood Group</label>
            <select value={form.bloodGroup} onChange={e => setForm({ ...form, bloodGroup: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-[#f8f9fb] focus:outline-none focus:border-[#0B3C95]">
              <option value="">Select blood group</option>
              {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => <option key={bg}>{bg}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold text-[#0D1B2A] mb-1.5 block">Emergency Contact</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">+91</span>
              <input type="tel" placeholder="Emergency phone number" value={form.emergencyPhone}
                onChange={e => setForm({ ...form, emergencyPhone: e.target.value })}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-[#f8f9fb] focus:outline-none focus:border-[#0B3C95]" />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-[#0D1B2A] mb-1.5 block">Local Pharmacy</label>
            <input type="text" placeholder="Your preferred pharmacy name" value={form.localPharmacy}
              onChange={e => setForm({ ...form, localPharmacy: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-[#f8f9fb] focus:outline-none focus:border-[#0B3C95]" />
          </div>
        </div>

        <button onClick={handleSave} disabled={loading}
          className="w-full py-4 bg-[#0B3C95] text-white font-bold rounded-2xl text-sm disabled:opacity-50 active:scale-95 transition-all"
          style={{ boxShadow: '0 8px 25px rgba(11,60,149,0.3)' }}>
          {loading ? '⏳ Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

export default UserProfile;